import React, { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { ArrowLeft, Info, Coins, Sparkles } from 'lucide-react';

const SYMBOLS = ['\u25c6', '\u2605', '7', '\u2660', '\u2666', '\u2663'];
const COLORS = ['#FF204E', '#B026FF', '#E0B85A', '#7A18FF', '#D90035', '#2FBF71'];
// Deterministic demo outcomes only \u2014 no real gambling maths
const DEMO_OUTCOMES = [
  [0, 0, 0], [1, 3, 5], [2, 2, 4], [4, 4, 4], [3, 1, 0],
  [5, 5, 2], [1, 1, 1], [0, 4, 2], [2, 2, 2], [3, 5, 1],
];

export default function SlotDemo({ slot, onClose, onRound, capped = false }) {
  const Icon = Icons[slot.icon] || Icons.Dice5;
  const [reels, setReels] = useState([0, 1, 2]);
  const [spinning, setSpinning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rounds, setRounds] = useState(0);
  const [win, setWin] = useState(false);
  const [msg, setMsg] = useState('Spin to play a qualifying round.');
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setWin(false);
    setMsg('Provider RNG resolving...');
    let ticks = 0;
    timer.current = setInterval(() => {
      setReels([Math.floor(Math.random() * 6), Math.floor(Math.random() * 6), Math.floor(Math.random() * 6)]);
      ticks++;
      if (ticks > 12) {
        clearInterval(timer.current);
        const out = DEMO_OUTCOMES[idx % DEMO_OUTCOMES.length];
        setReels(out);
        const isWin = out[0] === out[1] && out[1] === out[2];
        setWin(isWin);
        setMsg(isWin ? 'Demo win \u2014 outcome supplied by provider RNG.' : 'No win this round. (Demo outcome)');
        setIdx((i) => i + 1);
        setRounds((r) => r + 1);
        setSpinning(false);
        onRound && onRound(isWin);
      }
    }, 85);
  };

  return (
    <div className="xc-fade-up">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onClose} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to slots
        </button>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/50">Provider Game Demo</span>
      </div>

      <div className="grid md:grid-cols-[1fr_260px] gap-5 items-start">
        {/* Reels */}
        <div className="relative rounded-2xl p-6 overflow-hidden border border-[#FF204E]/20" style={{ background: 'linear-gradient(160deg,#151119,#0D0B10)' }}>
          <div className="absolute inset-0" style={{ background: `radial-gradient(70% 60% at 50% 20%, ${win ? 'rgba(224,184,90,0.22)' : 'rgba(122,24,255,0.16)'}, transparent 60%)` }} />
          <div className="relative flex items-center gap-2 mb-4">
            <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: slot.grad }}><Icon className="h-4 w-4 text-white" /></span>
            <span className="font-display font-700 text-white">{slot.title}</span>
          </div>
          <div className="relative grid grid-cols-3 gap-3">
            {reels.map((s, i) => (
              <div key={i} className="aspect-square rounded-xl grid place-items-center relative overflow-hidden"
                style={{ background: 'linear-gradient(160deg,#1c1626,#0D0B10)', border: `1px solid ${COLORS[s]}${win ? 'aa' : '55'}` }}>
                <span className={`font-display font-700 text-4xl md:text-5xl ${spinning ? 'blur-[1.5px]' : ''}`} style={{ color: COLORS[s], textShadow: `0 0 24px ${COLORS[s]}` }}>{SYMBOLS[s]}</span>
              </div>
            ))}
          </div>
          <div className={`relative mt-4 text-center text-sm ${win ? 'text-[#E0B85A]' : 'text-white/55'}`}>
            {win && <Sparkles className="h-4 w-4 inline mr-1.5 -mt-0.5" />}{msg}
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg px-3 py-2.5 bg-white/[0.04] border border-white/10"><div className="text-[10px] text-white/40 uppercase">Balance</div><div className="text-white font-600 text-sm">€183.40</div></div>
            <div className="rounded-lg px-3 py-2.5 bg-white/[0.04] border border-white/10"><div className="text-[10px] text-white/40 uppercase">Stake</div><div className="text-white font-600 text-sm">€1.00</div></div>
          </div>
          <div className="rounded-lg px-3 py-2.5 bg-white/[0.04] border border-white/10 flex items-center justify-between">
            <span className="text-xs text-white/50 flex items-center gap-1.5"><Coins className="h-3.5 w-3.5 text-[#E0B85A]" /> Rounds this session</span>
            <span className="text-white font-600 text-sm">{rounds}</span>
          </div>
          <button onClick={spin} disabled={spinning}
            className="btn-neon rounded-xl w-full py-4 font-700 tracking-widest disabled:opacity-60 disabled:cursor-not-allowed">
            {spinning ? 'SPINNING...' : 'SPIN'}
          </button>
          {capped ? (
            <div className="rounded-lg p-3 border border-[#E0B85A]/30 bg-[#E0B85A]/8 text-xs text-[#E0B85A] flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Contribution cap reached (20/20). Extra play no longer adds mission value.
            </div>
          ) : (
            <p className="text-[11px] text-white/40 leading-relaxed flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Each settled round contributes to your capped mission total. The provider controls the outcome — Xincity only reacts.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
